import type { Market } from '@packages/locale/types/locales.ts'

const BOOKING_PROCESS_MIN_TIMEOUT_MS = 120 * 1000

const DISCOUNT_CODE_VALIDATION_TIMEOUT_MS = 20 * 1000

// Read-only booking fetches retry once per second on instant network errors (see IT-2112).
const BOOKING_READ_NETWORK_RETRIES = 2
const BOOKING_READ_RETRY_DELAY_MS = 1000

// Browsers refuse a keepalive request whose body is above 64 KiB, a budget shared with every
// other keepalive request in flight (Sentry's transport uses them too).
const ERROR_REPORT_KEEPALIVE_MAX_BYTES = 32 * 1024

const FLIGHT_RELEASE_WINDOW_MONTHS = 11

/**
 * On the Almuñécar long-stay trips the Granada and Alhambra excursions run on the same day, so a booking cannot take both
 * The extras ids differ between departures, so the excursions are recognised by name.
 */
const ALMUNECAR_SAME_DAY_EXCURSION_NAME_PARTS = ['granada', 'alhambra']

const PAYMENT_ID_REGEX = /^B_.+_.+_.+$/

const BIRTHDATE_FROM_SSN_REGEX_PER_LOCALE: Record<Market, { pattern: string; order: 'YMD' | 'DMY' }> = {
  'sv': { pattern: '(19|20)(\\d{2})(\\d{2})(\\d{2})', order: 'YMD' },
  'no': { pattern: '(\\d{2})(\\d{2})(\\d{2})', order: 'DMY' },
  'da': { pattern: '(\\d{2})(\\d{2})(\\d{2})', order: 'DMY' },
  'en': { pattern: '(19|20)(\\d{2})(\\d{2})(\\d{2})', order: 'YMD' },
  'zz': { pattern: '(19|20)(\\d{2})(\\d{2})(\\d{2})', order: 'YMD' },
  'zt': { pattern: '(19|20)(\\d{2})(\\d{2})(\\d{2})', order: 'YMD' },
  'xx': { pattern: '(19|20)(\\d{2})(\\d{2})(\\d{2})', order: 'YMD' },
}

const DEFAULT_PHONE_COUNTRY_CODE_PER_MARKET: Record<Market, number> = {
  'sv': 46,
  'no': 47,
  'da': 45,
  'en': 1,
  'zz': 46,
  'zt': 46,
  'xx': 46,
}

const ZIP_CODE_LENGTH_PER_MARKET: Partial<Record<Market, number>> = {
  'sv': 5,
  'no': 4,
  'da': 4,
  'xx': 5,
}

const ZIP_CODE_LENGTH_PER_COUNTRY: Record<string, number> = {
  'SE': 5,
  'FI': 5,
  'NO': 4,
  'DK': 4,
}

enum PaxType {
  Manager = 'manager',
  Traveler = 'traveler',
}

enum JourneyDirection {
  Outbound = 'outbound',
  Homebound = 'homebound',
}

enum OrderStatusContext {
  Giftcard = 'giftcard',
  Booking = 'booking',
  Customerinfo = 'customerinfo',
}

enum BookingConfirmationStatus {
  StillProcessing = 'still_processing',
  ExternalUrlConfirmation = 'external_url_confirmation',
  NotConfirmed = 'not_confirmed',
  ConfirmedFullyPaid = 'confirmed_fully_paid',
  ConfirmedMorePaymentRequired = 'confirmed_more_payment_required',
}

enum LocalStorageKey {
  BookingConfirmationSent = 'rbbc',
}

enum SentryTagType {
  Tracking = 'TRACKING',
}

export {
  BOOKING_PROCESS_MIN_TIMEOUT_MS,
  DISCOUNT_CODE_VALIDATION_TIMEOUT_MS,
  BOOKING_READ_NETWORK_RETRIES,
  BOOKING_READ_RETRY_DELAY_MS,
  ERROR_REPORT_KEEPALIVE_MAX_BYTES,
  FLIGHT_RELEASE_WINDOW_MONTHS,
  ALMUNECAR_SAME_DAY_EXCURSION_NAME_PARTS,
  BIRTHDATE_FROM_SSN_REGEX_PER_LOCALE,
  PAYMENT_ID_REGEX,
  DEFAULT_PHONE_COUNTRY_CODE_PER_MARKET,
  ZIP_CODE_LENGTH_PER_MARKET,
  ZIP_CODE_LENGTH_PER_COUNTRY,
  PaxType,
  OrderStatusContext,
  BookingConfirmationStatus,
  LocalStorageKey,
  SentryTagType,
  JourneyDirection,
}